import { getBlogImagePaths } from '../utils/image-utils.js';
import { createIconElement } from '../utils/icon.js';
import { preloadAllLightboxImages } from '../utils/cache/index.js';
import { enableCarouselNavigation } from '../utils/gesture-utils.js';


let currentLightbox = null;

export async function openLightbox({ images, initialIndex = 0 }) {
    if (currentLightbox) {
        currentLightbox.remove();
    }

    // Preload in background - don't await!
    preloadAllLightboxImages(images, false);

    // Create overlay
    const overlay = document.createElement('div');
    overlay.className = 'lightbox-overlay fade-in';

    let currentIndex = initialIndex;

    let gesturesCleanup = null;
    let zoomCleanup = null;

    // Zoom state for current image
    let scale = 1;
    let translateX = 0;
    let translateY = 0;
    let isPinching = false;
    let lastTapTime = 0;

    // Close Handler
    const close = () => {
        overlay.classList.add('fade-out');
        overlay.addEventListener('animationend', () => {
            if (overlay.parentElement) {
                document.body.removeChild(overlay);
            }
            currentLightbox = null;
        }, { once: true });

        // Remove key listener
        document.removeEventListener('keydown', handleKey);

        // Cleanup gestures
        if (typeof gesturesCleanup === 'function') {
            gesturesCleanup();
            gesturesCleanup = null;
        }

        // Cleanup zoom
        if (typeof zoomCleanup === 'function') {
            zoomCleanup();
            zoomCleanup = null;
        }
    };

    // Navigation Handler
    const navigate = (direction) => {
        // Reset zoom when navigating
        resetZoom();
        currentIndex = (currentIndex + direction + images.length) % images.length;
        updateImage();
    };

    // Reset zoom state
    const resetZoom = () => {
        scale = 1;
        translateX = 0;
        translateY = 0;
        const currentImg = imgWrapper.querySelector('.lightbox-image[style*="display"]');
        if (currentImg) {
            currentImg.style.transform = '';
            currentImg.style.cursor = 'zoom-in';
        }
    };

    // Apply zoom transform to image
    const applyZoom = (img) => {
        img.style.transform = `scale(${scale}) translate(${translateX}px, ${translateY}px)`;
        img.style.cursor = scale > 1 ? 'zoom-out' : 'zoom-in';
    };

    // Enable zoom on image
    const enableZoom = (img) => {
        let startDistance = 0;
        let startScale = 1;
        let startX = 0;
        let startY = 0;
        let isDragging = false;

        // Double-tap to zoom
        img.addEventListener('click', (e) => {
            if (isPinching || isDragging) return;

            const now = Date.now();
            const timeSinceLastTap = now - lastTapTime;
            lastTapTime = now;

            if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
                // Double tap detected - toggle between normal and zoomed
                if (scale === 1) {
                    scale = 2.5;
                    applyZoom(img);
                } else {
                    // Already zoomed - reset to normal
                    scale = 1;
                    translateX = 0;
                    translateY = 0;
                    applyZoom(img);
                }
            }
        });

        // Pinch to zoom (mobile)
        img.addEventListener('touchstart', (e) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                isPinching = true;
                startDistance = Math.hypot(
                    e.touches[0].clientX - e.touches[1].clientX,
                    e.touches[0].clientY - e.touches[1].clientY
                );
                startScale = scale;
            } else if (e.touches.length === 1 && scale > 1) {
                isDragging = true;
                startX = e.touches[0].clientX - translateX;
                startY = e.touches[0].clientY - translateY;
            }
        });

        img.addEventListener('touchmove', (e) => {
            if (e.touches.length === 2 && isPinching) {
                e.preventDefault();
                const distance = Math.hypot(
                    e.touches[0].clientX - e.touches[1].clientX,
                    e.touches[0].clientY - e.touches[1].clientY
                );
                scale = Math.min(Math.max(startScale * (distance / startDistance), 1), 4);
                applyZoom(img);
            } else if (e.touches.length === 1 && isDragging && scale > 1) {
                e.preventDefault();
                translateX = e.touches[0].clientX - startX;
                translateY = e.touches[0].clientY - startY;
                applyZoom(img);
            }
        });

        img.addEventListener('touchend', () => {
            isPinching = false;
            isDragging = false;
            if (scale < 1) {
                resetZoom();
            }
        });

        // Mouse wheel zoom (desktop)
        img.addEventListener('wheel', (e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? 0.9 : 1.1;
            scale = Math.min(Math.max(scale * delta, 1), 4);

            if (scale === 1) {
                resetZoom();
            } else {
                applyZoom(img);
            }
        }, { passive: false });

        // Mouse drag when zoomed (desktop)
        img.addEventListener('mousedown', (e) => {
            if (scale > 1) {
                isDragging = true;
                startX = e.clientX - translateX;
                startY = e.clientY - translateY;
                img.style.cursor = 'grabbing';
            }
        });

        document.addEventListener('mousemove', (e) => {
            if (isDragging && scale > 1) {
                translateX = e.clientX - startX;
                translateY = e.clientY - startY;
                applyZoom(img);
            }
        });

        document.addEventListener('mouseup', () => {
            if (isDragging) {
                isDragging = false;
                img.style.cursor = scale > 1 ? 'zoom-out' : 'zoom-in';
            }
        });

        return () => {
            // Cleanup (not really needed as image gets removed)
        };
    };

    // Key Handler
    const handleKey = (e) => {
        if (e.key === 'Escape') close();
        if (e.key === 'ArrowLeft') navigate(-1);
        if (e.key === 'ArrowRight') navigate(1);
    };
    document.addEventListener('keydown', handleKey);

    // UI Elements
    overlay.innerHTML = `
        <button class="lightbox-close" aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
        </button>
        
        <div class="lightbox-content">
            <div class="lightbox-image-wrapper">
                <!-- Image injected here -->
            </div>
        </div>

        ${images.length > 1 ? `
            <button class="lightbox-arrow left" aria-label="Previous">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
            </button>
            <button class="lightbox-arrow right" aria-label="Next">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
            </button>
            <div class="lightbox-dots">
                ${images.map((_, idx) =>
        `<button class="lightbox-dot ${idx === initialIndex ? 'active' : ''}" data-index="${idx}"></button>`
    ).join('')}
            </div>
        ` : ''}
    `;

    const imgWrapper = overlay.querySelector('.lightbox-image-wrapper');
    const dotsContainer = overlay.querySelector('.lightbox-dots');

    // Pre-create picture and copyright elements to reserve layout space
    // This prevents layout shift on first image load
    const picturePlaceholder = document.createElement('picture');
    picturePlaceholder.className = 'lightbox-picture-placeholder';
    // Add default loader for the first image
    const initialLoader = document.createElement('div');
    initialLoader.className = 'lightbox-loader initial-lightbox-loader';
    imgWrapper.appendChild(picturePlaceholder);
    imgWrapper.appendChild(initialLoader);

    const copyrightPlaceholder = document.createElement('div');
    copyrightPlaceholder.className = 'lightbox-copyright-placeholder';
    copyrightPlaceholder.style.visibility = 'hidden'; // Hidden but takes up space
    imgWrapper.appendChild(copyrightPlaceholder);

    // Render Image Function - Reuses existing elements to prevent re-fetching and layout shift
    const updateImage = () => {
        const imgData = images[currentIndex];
        const imageId = imgData.id;
        // Use blog images for higher resolution
        const paths = getBlogImagePaths(imageId, 'webp');

        // Remove initial placeholder loader on first real update
        const firstLoader = imgWrapper.querySelector('.initial-lightbox-loader');
        if (firstLoader) firstLoader.remove();

        // Check if this image is already rendered (reuse if exists)
        let picture = imgWrapper.querySelector(`picture[data-image-id="${imageId}"]`);
        let copyrightDiv = imgWrapper.querySelector(`.lightbox-copyright[data-image-id="${imageId}"]`);

        if (picture) {
            // Image already exists in DOM - just show it (no new network request!)
            // Hide all other images
            imgWrapper.querySelectorAll('picture').forEach(p => {
                p.style.display = 'none';
            });
            imgWrapper.querySelectorAll('.lightbox-copyright').forEach(c => {
                c.style.display = 'none';
            });

            // Show current image
            picture.style.display = '';
            if (copyrightDiv) {
                copyrightDiv.style.display = '';
            }

            // RESET ZOOM STATE - image should always start fresh
            const img = picture.querySelector('img');
            if (img) {
                img.style.transform = 'scale(1) translate(0px, 0px)';
                img.style.cursor = 'zoom-in';
                // Success safety check for reused images
                if (img.complete || img.naturalWidth > 0) {
                    img.classList.add('lightbox-image-loaded');
                }
            }

            // Update dots and return early
            if (dotsContainer) {
                dotsContainer.querySelectorAll('.lightbox-dot').forEach((dot, idx) => {
                    dot.classList.toggle('active', idx === currentIndex);
                });
            }
            return; // Early return - no new image creation needed!
        }

        // Hide all existing images (including placeholders)
        imgWrapper.querySelectorAll('picture').forEach(p => {
            p.style.display = 'none';
        });
        imgWrapper.querySelectorAll('.lightbox-copyright').forEach(c => {
            c.style.display = 'none';
        });

        // Create new picture element (only if not already in DOM)
        picture = document.createElement('picture');
        picture.setAttribute('data-image-id', imageId); // Mark for reuse
        picture.style.display = '';

        const source = document.createElement('source');
        source.srcset = paths.webp;
        source.type = 'image/webp';
        picture.appendChild(source);

        const img = document.createElement('img');
        img.alt = imgData.alt_text || '';
        img.className = 'lightbox-image';
        img.loading = 'eager';

        // Add loading spinner logic
        const loader = document.createElement('div');
        loader.className = 'lightbox-loader';

        img.onload = () => {
            img.classList.add('lightbox-image-loaded');
        };

        // Set src AFTER onload to catch cached hits
        img.src = paths.webp;

        // Immediate check for cache hits
        if (img.complete) {
            img.classList.add('lightbox-image-loaded');
        }

        picture.appendChild(img);
        picture.appendChild(loader);

        // Use DocumentFragment to batch DOM updates - prevents layout shift
        const fragment = document.createDocumentFragment();
        fragment.appendChild(picture);

        if (imgData.copyright) {
            copyrightDiv = document.createElement('div');
            copyrightDiv.className = 'lightbox-copyright';
            copyrightDiv.setAttribute('data-image-id', imageId); // Mark for reuse
            copyrightDiv.style.visibility = 'visible'; // Make visible if it was hidden

            const cameraIcon = createIconElement('camera-solid', 'lightbox-copyright-icon');
            copyrightDiv.appendChild(cameraIcon);

            if (imgData.copyright_url) {
                const link = document.createElement('a');
                link.href = imgData.copyright_url;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.textContent = imgData.copyright;
                copyrightDiv.appendChild(link);
            } else {
                copyrightDiv.appendChild(document.createTextNode(imgData.copyright));
            }
            fragment.appendChild(copyrightDiv);
        }

        // Append both at once - browser calculates layout once, no shift!
        imgWrapper.appendChild(fragment);

        // Enable zoom on the newly created image
        zoomCleanup = enableZoom(img);

        // Update Dots (dots are already created in HTML, just update active state)
        if (dotsContainer) {
            dotsContainer.querySelectorAll('.lightbox-dot').forEach((dot, idx) => {
                dot.classList.toggle('active', idx === currentIndex);
            });
        }
    };

    // Event Listeners
    overlay.querySelector('.lightbox-close').onclick = close;

    // Close on backdrop click (but not image)
    overlay.onclick = (e) => {
        if (e.target === overlay || e.target.classList.contains('lightbox-content')) {
            close();
        }
    };

    if (images.length > 1) {
        overlay.querySelector('.lightbox-arrow.left').onclick = (e) => {
            e.stopPropagation();
            navigate(-1);
        };
        overlay.querySelector('.lightbox-arrow.right').onclick = (e) => {
            e.stopPropagation();
            navigate(1);
        };

        // Dot click handlers (dots are already in DOM)
        dotsContainer.querySelectorAll('.lightbox-dot').forEach((dot, idx) => {
            dot.onclick = (e) => {
                e.stopPropagation();
                currentIndex = idx;
                updateImage();
            };
        });

        // Enable keyboard navigation ONLY (no swipe to avoid zoom gesture conflicts)
        // Users can use arrows on screen for navigation
        requestAnimationFrame(() => {
            if (!overlay.isConnected) return;
            const isActive = () => overlay.isConnected;
            const onNavigateLeft = () => navigate(-1);
            const onNavigateRight = () => navigate(1);
            gesturesCleanup = enableCarouselNavigation(
                imgWrapper,
                onNavigateLeft,
                onNavigateRight,
                {
                    enableSwipe: false, // Disabled to avoid conflicts with pinch-to-zoom
                    enableKeyboard: true,
                    isActive,
                    ignoreCardsBoundary: true
                }
            );
        });
    }

    // Mount
    document.body.appendChild(overlay);
    currentLightbox = overlay;

    // Init
    updateImage();
}

